import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/core/components/Auth/AuthContext";
import { BUSINESS_NAME } from "@/core/constants/business";
import ProfileHeader from "./ProfileHeader";
import PersonalInfoSection from "./PersonalInfoSection";
import CommunicationPreferences from "./CommunicationPreferences";
import SecuritySection from "./SecuritySection";
import LoadingSpinner from "./LoadingSpinner";
import ErrorMessage from "./ErrorMessage";
import UserModel from "./types";
import { fetchUserProfile, updateUserProfile } from "./profile.service";

const ProfileDashboard: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [profileData, setProfileData] = useState<UserModel | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Redirect if not authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
    }
  }, [isAuthenticated, navigate]);

  // Fetch user profile data
  useEffect(() => {
    const getUserProfile = async () => {
      try {
        console.group("🔄 ProfileDashboard: Fetching Profile");
        console.log("Authentication state:", { isAuthenticated, user });

        setIsLoading(true);
        setError(null);

        if (user?.email) {
          console.log("Calling fetchUserProfile for:", user.email);
          const data = await fetchUserProfile(user.email);
          console.log("Received profile data:", data);

          // Ensure all required fields are present with defaults if missing
          const safeData: UserModel = {
            email: data.email || user.email,
            firstName: data.firstName || "User",
            lastName: data.lastName || "",
            phone: data.phone || "",
            birthDate: data.birthDate || new Date().toISOString().split("T")[0],
            isEmployee: Boolean(data.isEmployee),
            eventUpdates: Boolean(data.eventUpdates),
            promoOffers: Boolean(data.promoOffers),
            textMessages: Boolean(data.textMessages),
          };

          console.log("Sanitized profile data:", safeData);
          setProfileData(safeData);
        } else {
          console.warn("No user email available");
        }
        console.groupEnd();
      } catch (err) {
        console.error("Error fetching profile:", err);
        setError(
          "Unable to load your profile information. Please try again later."
        );
      } finally {
        setIsLoading(false);
      }
    };

    if (isAuthenticated) {
      getUserProfile();
    }
  }, [isAuthenticated, user]);

  const handleToggleEdit = () => {
    setIsEditing(!isEditing);
    setSuccessMessage(null);
  };

  const handleUpdateProfile = async (updatedData: Partial<UserModel>) => {
    try {
      console.group("✏️ ProfileDashboard: Updating Profile");
      console.log("Current profile data:", profileData);
      console.log("Update data:", updatedData);

      setIsSaving(true);
      setError(null);

      if (profileData?.email) {
        const updated = await updateUserProfile(profileData.email, updatedData);
        console.log("Update successful, new data:", updated);

        // Ensure we preserve the isEmployee status
        const mergedData = {
          ...profileData,
          ...updated,
          isEmployee: profileData.isEmployee, // Keep original isEmployee value
        };
        console.log("Merged profile data:", mergedData);

        setProfileData(mergedData);
        setIsEditing(false);
        setSuccessMessage("Your profile has been updated successfully!");

        // Auto-dismiss success message after 5 seconds
        setTimeout(() => setSuccessMessage(null), 5000);
      }
      console.groupEnd();
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Unable to update your profile. Please try again later.");
    } finally {
      setIsSaving(false);
    }
  };

  // Add debug logging for render
  useEffect(() => {
    if (profileData) {
      console.group("🖥️ ProfileDashboard: Render");
      console.log("Current profile data:", profileData);
      console.log("isEmployee value:", profileData.isEmployee);
      console.log("isEmployee type:", typeof profileData.isEmployee);
      console.groupEnd();
    }
  }, [profileData]);

  if (!isAuthenticated) {
    return null; // Redirect handled by useEffect
  }

  return (
    <>
      <Helmet>
        <title>My Profile | {BUSINESS_NAME}</title>
        <meta
          name="description"
          content="Manage your personal profile, preferences, and account settings."
        />
      </Helmet>

      <section className="bg-gradient-to-b from-law-black to-gray-900 text-white py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-3xl font-bold mb-8">My Profile</h1>

          {isLoading ? (
            <LoadingSpinner message="Loading your profile information..." />
          ) : error ? (
            <ErrorMessage message={error} />
          ) : profileData ? (
            <div className="space-y-8">
              {successMessage && (
                <div className="bg-green-50 border-l-4 border-green-500 p-4 text-green-700 mb-6">
                  <p>{successMessage}</p>
                </div>
              )}

              <ProfileHeader
                profileData={profileData}
                isEmployee={Boolean(profileData.isEmployee)}
                isEditing={isEditing}
                onToggleEdit={handleToggleEdit}
              />

              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2 space-y-8">
                  <PersonalInfoSection
                    profileData={profileData}
                    isEditing={isEditing}
                    onUpdate={handleUpdateProfile}
                    isSaving={isSaving}
                  />

                  <CommunicationPreferences
                    profileData={profileData}
                    isEditing={isEditing}
                    onUpdate={handleUpdateProfile}
                    isSaving={isSaving}
                  />
                </div>

                <div className="space-y-8">
                  <SecuritySection
                    profileData={profileData}
                    onLogout={logout}
                  />
                </div>
              </div>
            </div>
          ) : (
            <ErrorMessage message="No profile information found. Please contact support." />
          )}
        </div>
      </section>
    </>
  );
};

export default ProfileDashboard;
